/**
 * 设备指纹和会话ID生成工具
 * 用于风控系统识别设备和会话
 * 使用增强的设备指纹算法，包括 Canvas、WebGL 和音频指纹
 */

import CryptoJS from 'crypto-js'

/**
 * 设备指纹管理类
 */
class DeviceFingerprint {
  constructor() {
    this.envData = null
    this.fingerprint = null
  }

  // =============================================
  // 1. 增强的设备指纹生成
  // =============================================

  async collectEnvironmentData() {
    this.envData = {
      // 基础信息
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      languages: navigator.languages ? navigator.languages.join(',') : '',
      hardwareConcurrency: navigator.hardwareConcurrency || 0,
      deviceMemory: navigator.deviceMemory || 0,

      // 屏幕信息
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      screenColorDepth: window.screen.colorDepth,
      screenPixelDepth: window.screen.pixelDepth,
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,

      // 时区和时间
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),

      // 浏览器特性
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack || 'unspecified',
      maxTouchPoints: navigator.maxTouchPoints || 0,

      // Canvas 指纹
      canvasFingerprint: this.getCanvasFingerprint(),

      // WebGL 指纹
      webglFingerprint: this.getWebGLFingerprint(),

      // 音频指纹
      audioFingerprint: 'loading...'
    }

    // 异步获取音频指纹
    this.envData.audioFingerprint = await this.getAudioFingerprint()
  }

  // Canvas 指纹
  getCanvasFingerprint() {
    try {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      canvas.width = 200
      canvas.height = 50

      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.textBaseline = 'alphabetic'
      ctx.fillStyle = '#f60'
      ctx.fillRect(125, 1, 62, 20)
      ctx.fillStyle = '#069'
      ctx.fillText('Canvas Fingerprint 🔒', 2, 15)
      ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
      ctx.fillText('Canvas Fingerprint 🔒', 4, 17)

      return canvas.toDataURL().substring(0, 100)
    } catch (e) {
      return 'unsupported'
    }
  }

  // WebGL 指纹
  getWebGLFingerprint() {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

      if (!gl) return 'unsupported'

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
      if (debugInfo) {
        return {
          vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        }
      }

      return gl.getParameter(gl.VERSION)
    } catch (e) {
      return 'unsupported'
    }
  }

  // 音频指纹（带超时）
  async getAudioFingerprint() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return 'unsupported'

      // 添加超时机制，2秒后超时
      const audioPromise = new Promise((resolve) => {
        const context = new AudioContext()
        const oscillator = context.createOscillator()
        const analyser = context.createAnalyser()
        const gainNode = context.createGain()
        const scriptProcessor = context.createScriptProcessor(4096, 1, 1)

        gainNode.gain.value = 0 // 静音
        oscillator.type = 'triangle'
        oscillator.connect(analyser)
        analyser.connect(scriptProcessor)
        scriptProcessor.connect(gainNode)
        gainNode.connect(context.destination)
        oscillator.start(0)

        scriptProcessor.onaudioprocess = function(event) {
          const output = event.outputBuffer.getChannelData(0)
          const hash = output.slice(0, 30).join('').substring(0, 50)
          oscillator.stop()
          scriptProcessor.disconnect()
          context.close()
          resolve(hash)
        }
      })

      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve('timeout'), 2000)
      })

      return await Promise.race([audioPromise, timeoutPromise])
    } catch (e) {
      console.warn('[Fingerprint] 音频指纹生成失败:', e)
      return 'unsupported'
    }
  }

  // 生成综合设备指纹
  generateEnhancedFingerprint() {
    const data = JSON.stringify(this.envData)
    return CryptoJS.SHA256(data).toString().substring(0, 32)
  }

  // 获取或生成设备指纹
  async getFingerprint() {
    // 如果已经生成过指纹，直接返回
    if (this.fingerprint) {
      return this.fingerprint
    }

    // 尝试从 localStorage 获取
    const stored = localStorage.getItem('deviceFingerprint')
    if (stored) {
      this.fingerprint = stored
      return stored
    }

    // 收集环境数据并生成新指纹
    await this.collectEnvironmentData()
    this.fingerprint = this.generateEnhancedFingerprint()

    // 保存到 localStorage
    localStorage.setItem('deviceFingerprint', this.fingerprint)

    return this.fingerprint
  }
}

// 创建单例实例
const fingerprintInstance = new DeviceFingerprint()

/**
 * 获取设备指纹
 * @returns {Promise<string>} 设备指纹
 */
export async function getDeviceFingerprint() {
  return await fingerprintInstance.getFingerprint()
}

/**
 * 获取会话ID
 * @returns {string} 会话ID
 */
export function getSessionId() {
  let sessionId = sessionStorage.getItem('sessionId')

  if (!sessionId) {
    // 生成新的会话ID
    sessionId = `session_${Date.now()}_${generateRandomString(9)}`
    sessionStorage.setItem('sessionId', sessionId)
  }

  return sessionId
}

/**
 * 生成随机字符串
 * @param {number} length - 字符串长度
 * @returns {string} 随机字符串
 */
function generateRandomString(length) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 清除设备指纹（用于测试或登出）
 */
export function clearDeviceFingerprint() {
  localStorage.removeItem('deviceFingerprint')
  fingerprintInstance.fingerprint = null
  fingerprintInstance.envData = null
}

/**
 * 清除会话ID（用于测试或登出）
 */
export function clearSessionId() {
  sessionStorage.removeItem('sessionId')
}

/**
 * 获取环境数据（用于调试）
 * @returns {Promise<Object>} 环境数据
 */
export async function getEnvironmentData() {
  if (!fingerprintInstance.envData) {
    await fingerprintInstance.collectEnvironmentData()
  }
  return fingerprintInstance.envData
}
